import { auth } from "../firebase";

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:5000";

async function handleResponse(response, fallbackMessage) {
  let data = {};
  try { data = await response.json(); } catch { data = {}; }
  if (!response.ok) throw new Error(data.error || fallbackMessage);
  return data;
}

async function getAuthHeaders(includeJson = false) {
  const headers = {};
  if (includeJson) headers["Content-Type"] = "application/json";
  await new Promise((resolve) => {
    const unsub = auth.onAuthStateChanged((u) => { unsub(); resolve(u); });
  });
  const user = auth.currentUser;
  if (!user) return headers;
  const token = await user.getIdToken();
  headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

export async function ensureUser() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/ensure-user`, { method: "POST", headers });
  return handleResponse(response, "Failed to ensure user");
}

export async function getMe() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/me`, { headers });
  return handleResponse(response, "Failed to load account");
}

export async function updateProfile(payload) {
  const headers = await getAuthHeaders(true);
  const response = await fetch(`${API_BASE}/update-profile`, {
    method: "POST", headers, body: JSON.stringify(payload),
  });
  return handleResponse(response, "Failed to update profile");
}

export async function createCheckoutSession() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/create-checkout-session`, { method: "POST", headers });
  return handleResponse(response, "Failed to create checkout session");
}

export async function finalizeCheckoutSession(sessionId) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/checkout-session-status?session_id=${sessionId}`, { headers });
  return handleResponse(response, "Failed to finalize checkout");
}

export async function getStats() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/stats`, { headers });
  return handleResponse(response, "Failed to load stats");
}

export async function getCalendar(month) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/calendar?month=${month}`, { headers });
  return handleResponse(response, "Failed to load calendar");
}

export async function getTradesByDate(date) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/trades-by-date?date=${date}`, { headers });
  return handleResponse(response, "Failed to load trades");
}

export async function getWeeklySummary(date) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/weekly-summary?date=${date}`, { headers });
  return handleResponse(response, "Failed to load weekly summary");
}

export async function getAIOverview(date = "") {
  const headers = await getAuthHeaders();
  const url = date ? `${API_BASE}/ai-overview?date=${date}` : `${API_BASE}/ai-overview`;
  const response = await fetch(url, { headers });
  return handleResponse(response, "Failed to load AI overview");
}

export async function getAIDailyReview(date) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/ai-daily-review?date=${date}`, { headers });
  return handleResponse(response, "Failed to load daily AI review");
}

export async function getAIWeeklyReview(date) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/ai-weekly-review?date=${date}`, { headers });
  return handleResponse(response, "Failed to load weekly AI review");
}

export async function createTrade(trade) {
  const headers = await getAuthHeaders(true);
  const response = await fetch(`${API_BASE}/trade`, {
    method: "POST", headers, body: JSON.stringify(trade),
  });
  return handleResponse(response, "Failed to save trade");
}

export async function createTradeWithScreenshot(trade, screenshotFile) {
  const headers = await getAuthHeaders();
  const formData = new FormData();
  formData.append("trade", JSON.stringify(trade));
  if (screenshotFile) formData.append("screenshot", screenshotFile);
  const response = await fetch(`${API_BASE}/trade-with-screenshot`, {
    method: "POST", headers, body: formData,
  });
  return handleResponse(response, "Failed to save trade with screenshot");
}

export async function deleteTrade(tradeId) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/trade/${tradeId}`, { method: "DELETE", headers });
  return handleResponse(response, "Failed to delete trade");
}

export async function createGroup(name) {
  const headers = await getAuthHeaders(true);
  const response = await fetch(`${API_BASE}/groups`, {
    method: "POST", headers, body: JSON.stringify({ name }),
  });
  return handleResponse(response, "Failed to create group");
}

export async function getGroups() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/groups`, { headers });
  return handleResponse(response, "Failed to load groups");
}

export async function deleteGroup(groupId) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/groups/${groupId}`, { method: "DELETE", headers });
  return handleResponse(response, "Failed to delete group");
}

export async function leaveGroup(groupId) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/groups/${groupId}/leave`, { method: "POST", headers });
  return handleResponse(response, "Failed to leave group");
}

export async function inviteToGroup(groupId, email) {
  const headers = await getAuthHeaders(true);
  const response = await fetch(`${API_BASE}/groups/${groupId}/invite`, {
    method: "POST", headers, body: JSON.stringify({ email }),
  });
  return handleResponse(response, "Failed to invite member");
}

export async function getPendingInvites() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/group-invites`, { headers });
  return handleResponse(response, "Failed to load invites");
}

export async function acceptGroupInvite(groupId) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/groups/${groupId}/accept-invite`, { method: "POST", headers });
  return handleResponse(response, "Failed to accept invite");
}

export async function declineGroupInvite(groupId) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/groups/${groupId}/decline-invite`, { method: "POST", headers });
  return handleResponse(response, "Failed to decline invite");
}

export async function getGroupMembers(groupId) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/groups/${groupId}/members`, { headers });
  return handleResponse(response, "Failed to load members");
}

export async function getGroupMemberStats(groupId, memberUid) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/groups/${groupId}/member/${memberUid}/stats`, { headers });
  return handleResponse(response, "Failed to load member stats");
}

export async function getGroupMemberCalendar(groupId, memberUid, month) {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/groups/${groupId}/member/${memberUid}/calendar?month=${month}`, { headers }
  );
  return handleResponse(response, "Failed to load member calendar");
}

export async function getGroupMemberDayReview(groupId, memberUid, date) {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/groups/${groupId}/member/${memberUid}/day-review?date=${date}`, { headers }
  );
  return handleResponse(response, "Failed to load member day review");
}

export async function getGroupComments(groupId, targetUid, date) {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/groups/${groupId}/comments?target_uid=${targetUid}&date=${date}`, { headers }
  );
  return handleResponse(response, "Failed to load comments");
}

export async function createGroupComment(groupId, payload) {
  const headers = await getAuthHeaders(true);
  const response = await fetch(`${API_BASE}/groups/${groupId}/comments`, {
    method: "POST", headers, body: JSON.stringify(payload),
  });
  return handleResponse(response, "Failed to post comment");
}